import { formatDistanceToNow } from 'date-fns';
import { useEffect, useState } from 'react';

import { type TimeFormat, timeParts, useOrgTimeFormat } from '@/lib/time';

export interface DateTimeValueLabelProps {
  value: string | null | undefined;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  /** Enable CSS truncation with ellipsis */
  truncate?: boolean;
}

const fontSizeClasses = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/**
 * Format absolute time for display.
 */
function formatAbsoluteTime(date: Date, format: TimeFormat): string {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...timeParts(format),
  });
}

/**
 * Display component for datetime values.
 *
 * Always shows the absolute date/time. If the value is within the last
 * 24 hours, appends relative time in brackets:
 *   "Feb 26, 2026, 2:30 PM (2 hours ago)"
 *
 * Empty values (null/undefined/empty string) render as "—". A non-empty
 * value that cannot be parsed as a date is rendered as the raw string
 * (matching the sibling DateValueLabel), so a visible source value is
 * never hidden.
 *
 * @example
 * <DateTimeValueLabel value="2024-11-26T14:30:00Z" />
 * // Recent: "Nov 26, 2024, 2:30 PM (about 2 hours ago)"
 * // Older:  "Nov 26, 2024, 2:30 PM"
 * <DateTimeValueLabel value="31/12/2026 14:00" />
 * // Unparseable: "31/12/2026 14:00" (raw string, not "—")
 */
export const DateTimeValueLabel: React.FC<DateTimeValueLabelProps> = ({
  value,
  fontSize = 'sm',
  className = '',
  truncate,
}) => {
  const { timeFormat } = useOrgTimeFormat();
  const [, setTick] = useState(0);
  const sizeClass = fontSizeClasses[fontSize];
  const truncateClasses = truncate
    ? 'block overflow-hidden text-ellipsis whitespace-nowrap'
    : '';

  // Auto-update relative time every 30 seconds for recent values
  useEffect(() => {
    if (!value) {
      return;
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return;
    }
    if (Math.abs(Date.now() - date.getTime()) >= TWENTY_FOUR_HOURS_MS) {
      return;
    }

    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 30000);

    return () => clearInterval(interval);
  }, [value]);

  if (!value) {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return (
        <span
          className={`${sizeClass} ${truncateClasses} ${className}`}
          title={truncate ? value : undefined}
        >
          {value}
        </span>
      );
    }

    const absoluteTime = formatAbsoluteTime(date, timeFormat);
    const isRecent =
      Math.abs(Date.now() - date.getTime()) < TWENTY_FOUR_HOURS_MS;

    return (
      <span
        className={`${sizeClass} ${truncateClasses} ${className}`}
        title={truncate ? absoluteTime : undefined}
      >
        {absoluteTime}
        {isRecent && (
          <span className='text-tertiary'>
            {' '}
            ({formatDistanceToNow(date, { addSuffix: true })})
          </span>
        )}
      </span>
    );
  } catch {
    return (
      <span
        className={`${sizeClass} ${truncateClasses} ${className}`}
        title={truncate ? value : undefined}
      >
        {value}
      </span>
    );
  }
};
